import { makeAutoObservable, reaction } from "mobx";
import { createRef } from "react";
import type { CircleLayerSpecification, MapRef, SourceSpecification } from "react-map-gl/mapbox";
// import type { Map as MapboxMap } from "mapbox-gl";
import { MarkersStore, type LngLatTuple } from "./MarkersStore";
import { MapFilterStore } from "./MapFilterStore";
import { ClusterMarkersStore } from "./ClusterMarkersStore";
import { MapBoxSource } from "./MapBoxSource";
import { MapPopupsStore } from "../MapPopups/MapPopupsStore";
import { RightClickMenuStore } from "../RightClickMenu/RightClickMenuStore";
import { resolveMapRightClickTarget } from "./mapRightClickTarget";
import { RainStore } from "../Rain/RainStore";
import { OverlaysStore } from "../Overlays/OverlaysStore";
import { ConnectionsStore } from "../Connections/ConnectionsStore";

export interface MarkerLayerConfig {
	paint?: CircleLayerSpecification["paint"];
	layout?: CircleLayerSpecification["layout"];
	minzoom?: number;
	maxzoom?: number;
}

export interface MapMarkersConfig<TData> {
	getCoordinates: (item: TData) => LngLatTuple;
	getId: (item: TData) => string;
	getProperties?: (item: TData) => Record<string, unknown>;
	layerSpec?: mapboxgl.CircleLayer;
}

export interface MarkerDataConfig<TData> {
	getCoordinates: (item: TData) => LngLatTuple;
	getId: (item: TData) => string;
	getProperties?: (item: TData) => Record<string, unknown>;
}

export interface ClusterConfig {
	sourceId?: string;
	clusterRadius?: number;
	clusterMaxZoom?: number;
	clusterLayerId?: string;
	clusterCountLayerId?: string;
}

export class MapStore<TData = any> {
	mapRef = createRef<MapRef>();

	isLoaded = false;

	markers: MarkersStore;
	clusters?: ClusterMarkersStore;

	onFetch?: () => Promise<void>;
	filters = new MapFilterStore();

	popupsStore = new MapPopupsStore();
	rightClickMenuStore = new RightClickMenuStore(resolveMapRightClickTarget);
	rainStore = new RainStore(this.mapRef, "markers-layer");
	overlaysStore = new OverlaysStore(this.mapRef);
	connectionsStore: ConnectionsStore;

	private source: MapBoxSource;
	private dataConfig?: MarkerDataConfig<TData>;
	private itemsById = new Map<string, TData>();

	constructor(
		layerConfig?: MarkerLayerConfig,
		dataConfig?: MarkerDataConfig<TData>,
		clustering?: ClusterConfig,
		onConnectionsChanged?: () => void | Promise<void>,
	) {
		this.connectionsStore = new ConnectionsStore(
			this.mapRef,
			id => this.getLngLat(id),
			this.popupsStore,
			onConnectionsChanged,
		);

		const sourceSpec: SourceSpecification = {
			type: "geojson",
			data: {
				type: "FeatureCollection",
				features: [],
			},
			...(clustering
				? {
						cluster: true,
						clusterRadius: clustering.clusterRadius ?? 30,
						clusterMaxZoom: clustering.clusterMaxZoom ?? 11,
					}
				: {}),
		};

		this.source = new MapBoxSource({
			id: "markers-source",
			spec: sourceSpec,
		});

		this.markers = new MarkersStore(this.source, "markers-layer", layerConfig);

		if (clustering) {
			this.clusters = new ClusterMarkersStore(this.source, {
				...clustering,
				sourceId: this.source.id,
			});
		}

		this.dataConfig = dataConfig;

		makeAutoObservable(this, {
			mapRef: false,
			selectedItem: false,
		});

		reaction(
			() => this.filters.snapshot,
			async () => {
				if (!this.isLoaded) return;
				if (!this.onFetch) return;

				await this.onFetch();
			},
			{
				fireImmediately: false,
			},
		);
	}

	// private get mapbox(): MapboxMap | null {
	// 	return this.mapRef.current?.getMap() ?? null;
	// }

	onLoad(mapRef: MapRef) {
		this.isLoaded = true;

		const map = mapRef.getMap();

		// attach markers (and any other child stores)
		this.source.attach(map);
		this.markers.attach(map);
		this.clusters?.attach(map);
		// Kick off the overlay catalog fetch — actual overlay data only loads on toggle.
		this.overlaysStore.attach();
		this.connectionsStore.attach();
		// No initial flyTo: the map starts at MapBase's initialViewState and
		// setMarkersAndFit(...) snaps to the actual data bbox once it arrives,
		// avoiding the visible "default zoom → data zoom" jump.
	}

	get selectedItem(): TData | undefined {
		const id = this.markers.selectedId;
		if (!id) return undefined;
		return this.itemsById.get(id);
	}

	get markersCount(): number {
		return this.itemsById.size;
	}

	getMarkerById = (id: string): TData | undefined => {
		return this.itemsById.get(id);
	};

	getLngLat = (id: string): LngLatTuple | null => {
		const item = this.itemsById.get(id);
		if (!item || !this.dataConfig) return null;
		return this.dataConfig.getCoordinates(item);
	};

	getMarkerIds = (): string[] => {
		return Array.from(this.itemsById.keys());
	};

	get selectedLngLat(): LngLatTuple | null {
		const item = this.selectedItem;
		if (!item || !this.dataConfig) return null;
		return this.dataConfig.getCoordinates(item);
	}

	setMarkers(data: TData[]) {
		if (!this.dataConfig) {
			console.warn("MapStore.setMarkers called without dataConfig");
			return;
		}
		const { getCoordinates, getId, getProperties } = this.dataConfig;

		this.markers.setMarkers(data, getCoordinates, getId, getProperties);
		this.clusters?.setMarkers(data, getCoordinates, getId, getProperties);

		this.itemsById.clear();
		for (const item of data) {
			const id = String(getId(item));
			this.itemsById.set(id, item);
		}
	}

	/**
	 * Sets markers and then fits the camera to them using dataConfig.getCoordinates.
	 * Use this on data refreshes that should also reframe the view (e.g. filter changes,
	 * perspective changes). fitToData is a no-op on empty data, so safe to call always.
	 */
	setMarkersAndFit(data: TData[]) {
		this.setMarkers(data);
		if (!this.dataConfig) return;
		this.fitToData(data, this.dataConfig.getCoordinates);
	}

	clearSelection() {
		this.markers.clearSelection();
	}

	flyTo(center: LngLatTuple, zoom = 14) {
		this.mapRef.current?.flyTo({ center, zoom, duration: 1000 });
	}

	fitToData<TData>(data: TData[], getCoordinates: (item: TData) => LngLatTuple, padding = 40) {
		const map = this.mapRef.current?.getMap();
		if (!map || data.length === 0) return;

		let minLng = Infinity;
		let minLat = Infinity;
		let maxLng = -Infinity;
		let maxLat = -Infinity;

		for (const item of data) {
			const [lng, lat] = getCoordinates(item);
			minLng = Math.min(minLng, lng);
			minLat = Math.min(minLat, lat);
			maxLng = Math.max(maxLng, lng);
			maxLat = Math.max(maxLat, lat);
		}

		map.fitBounds(
			[
				[minLng, minLat],
				[maxLng, maxLat],
			],
			{
				padding,
				duration: 800,
			},
		);
	}

	/**
	 * Swap the live layer config (paint/layout) at runtime.
	 * Useful when different perspectives style markers differently.
	 */
	setLayerConfig = (layerConfig: MarkerLayerConfig) => {
		this.markers.applyLayerConfig(layerConfig);
	};

	/**
	 * Swap the dataConfig at runtime. The next setMarkers() will use the new
	 * coordinate/id/properties accessors. Typed loosely on purpose: callers
	 * mixing perspectives across different item shapes own the contract that
	 * each dataConfig matches the items passed to setMarkers.
	 */
	setDataConfig = (dataConfig: MarkerDataConfig<any>) => {
		this.dataConfig = dataConfig as MarkerDataConfig<TData>;
	};

	reset = (opts?: { layerConfig?: MarkerLayerConfig; dataConfig?: MarkerDataConfig<any> }) => {
		this.markers.clearSelection();
		if (opts?.dataConfig) this.dataConfig = opts.dataConfig as MarkerDataConfig<TData>;
		if (opts?.layerConfig) this.markers.applyLayerConfig(opts.layerConfig);
		this.setMarkers([]); // empties the GeoJSON FC + clears itemsById
	};

	reattach = () => {
		const map = this.mapRef.current?.getMap();
		if (!map) return;
		this.markers.detach();
		this.clusters?.detach();
		this.markers.attach(map); // attaches source internally; idempotent
		this.clusters?.attach(map);
	};
}
